import { PRODUCTION_PLAN } from '../../constants';
import {
  DOWNLOAD_RETAIL_IST_OUTWARD_SAMPLE_URL,
  RETAIL_IST_OUTWARD_STOS_URL,
  RETAIL_IST_OUTWARD_UPLOAD_HISTORY_URL,
  UPLOAD_RETAIL_IST_OUTWARD_URL,
} from '../../utils/serviceUrls';
import StorageService from '../../utils/storageService';
import { USER_TOKEN_STORAGE_KEY } from '../LoginProvider/constants';

const DEFAULT_FILE_NAME = 'retail-ist-outward-sample.csv';
const DEFAULT_HISTORY_ERROR = 'Failed to fetch upload history';
const DEFAULT_UPLOAD_ERROR = 'Failed to upload IST outward file';

const getRequestHeaders = (requestedBy) => ({
  token: StorageService.getValue(USER_TOKEN_STORAGE_KEY),
  'X-REQUESTED-APP': PRODUCTION_PLAN,
  'X-REQUESTED-BY': requestedBy,
});

const getFileNameFromDisposition = (contentDisposition) => {
  const fileNameMatch =
    /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(contentDisposition);

  return fileNameMatch?.[1]
    ? decodeURIComponent(fileNameMatch[1].replaceAll('"', ''))
    : DEFAULT_FILE_NAME;
};

export const downloadRetailIstOutwardSample = async (requestedBy) => {
  const response = await fetch(DOWNLOAD_RETAIL_IST_OUTWARD_SAMPLE_URL, {
    method: 'GET',
    headers: getRequestHeaders(requestedBy),
  });

  if (!response.ok) {
    throw new Error('Failed to download sample CSV');
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition') || '';
  const fileName = getFileNameFromDisposition(contentDisposition);
  const fileUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = fileUrl;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(fileUrl);
};

export const fetchRetailIstOutwardUploadHistory = async (
  requestedBy,
  page,
  size,
) => {
  const queryParams = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  const url = `${RETAIL_IST_OUTWARD_UPLOAD_HISTORY_URL}?${queryParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getRequestHeaders(requestedBy),
  });

  if (!response.ok) {
    throw new Error(DEFAULT_HISTORY_ERROR);
  }

  return response.json();
};

export const fetchRetailIstOutwardStos = async (requestedBy, ids) => {
  const queryParams = new URLSearchParams({ ids: ids.join(',') });
  const url = `${RETAIL_IST_OUTWARD_STOS_URL}?${queryParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getRequestHeaders(requestedBy),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch STO details');
  }

  return response.json();
};

const parseErrorResponse = async (response, fallbackMessage) => {
  try {
    const data = await response.json();
    return (
      data?.message ||
      data?.errors?.[0]?.message ||
      data?.error ||
      fallbackMessage
    );
  } catch (error) {
    return fallbackMessage;
  }
};

export const uploadRetailIstOutwardFile = async (requestedBy, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(UPLOAD_RETAIL_IST_OUTWARD_URL, {
    method: 'POST',
    headers: getRequestHeaders(requestedBy),
    body: formData,
  });

  if (!response.ok) {
    const errorMessage = await parseErrorResponse(response, DEFAULT_UPLOAD_ERROR);
    throw new Error(errorMessage);
  }

  try {
    return await response.json();
  } catch (error) {
    return {
      message: 'IST outward file uploaded successfully',
    };
  }
};
